import { useEffect } from "react";
import { Link } from "react-router-dom";
import Cookies from "js-cookie";

import { useUserInvite } from "../../../hooks/useUser";
import { MSG } from "../../../constants/msg";

import { isNotEmpty, useForm } from "@mantine/form";
import {
  Alert,
  Button,
  Grid,
  Paper,
  Text,
  TextInput,
  Title,
  TypographyStylesProvider,
} from "@mantine/core";
import { FaExclamationCircle, FaArrowRight } from "react-icons/fa";
import { WelcomeFormValues, WelcomeProps } from "types/module/User/Onboarding";

const Welcome: React.FC<WelcomeProps> = ({
  isSubmitting,
  setIsSubmitting,
  setUser,
  setOnboardStep,
}) => {
  const form = useForm<WelcomeFormValues>({
    initialValues: {
      name: "",
      email: "",
    },
    validate: {
      name: isNotEmpty(MSG.VALIDATE.REQUIRED("Name")),
      email: (value: string) => {
        if (!value) return MSG.VALIDATE.REQUIRED("Email");
        return /^\S+@\S+\.\S+$/.test(value) ? null : MSG.VALIDATE.EMAIL;
      },
    },
  });

  const {
    mutate: userInviteMutate,
    data: userInviteData,
    isSuccess: userInviteIsSuccess,
    isError: userInviteIsError,
    error: userInviteError,
  } = useUserInvite();

  const handleEmailSubmit = (data: WelcomeFormValues) => {
    setIsSubmitting(true);

    setUser((prev) => ({
      ...prev,
      name: data.name,
      email: data.email,
    }));

    // @ts-ignore todo: for hooks ts migration
    userInviteMutate({
      name: data.name,
      email: data.email,
    });
  };

  useEffect(() => {
    if (userInviteIsSuccess || userInviteIsError) {
      if (userInviteIsSuccess && userInviteData) {
        const invitationId = userInviteData.data?.invitationId;

        if (userInviteData.data?.isFederationIdEmpty) {
          setUser((prev) => ({
            ...prev,
            isAuthUser: userInviteData.data.isAuthUser,
          }));

          setOnboardStep("REGISTERED");
        } else {
          if (invitationId) {
            setUser((prev) => ({
              ...prev,
              invitationId: invitationId,
              invitationDate: userInviteData.data?.inivitationDateTime
            }));

            setOnboardStep("INVITE");
          } else {
            setOnboardStep("REGISTER");
          }
        }

        form.reset();
      }
      setIsSubmitting(false);
    }
  }, [userInviteIsSuccess, userInviteIsError]);

  return (
    <>
      <Title ta="center" c="black">
        Welcome {Cookies.get("user_name")}!
      </Title>
      <Text c="dimmed" size="lg" ta="center" mt={5}>
        Who are we onboarding today?
      </Text>
      <Paper withBorder shadow="md" p={30} mt={20} radius="md">
        <form onSubmit={form.onSubmit((values: WelcomeFormValues) => handleEmailSubmit(values))}>
          {userInviteIsError && userInviteError?.response?.data?.message && (
            <Alert color="red" icon={<FaExclamationCircle />}>
              <TypographyStylesProvider>
                <div
                  dangerouslySetInnerHTML={{
                    __html: userInviteError?.response?.data?.message,
                  }}
                />
              </TypographyStylesProvider>
            </Alert>
          )}
          <Grid>
            <Grid.Col>
              <TextInput
                key="name"
                label="Name"
                placeholder="Enter Name"
                disabled={isSubmitting}
                {...form.getInputProps("name")}
              />
            </Grid.Col>
            <Grid.Col>
              <TextInput
                key="email"
                label="Email"
                placeholder="Enter Email"
                disabled={isSubmitting}
                {...form.getInputProps("email")}
              />
            </Grid.Col>
            <Grid.Col span={{ md: 8 }}>
              <Button
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting}
                rightSection={<FaArrowRight size={14} />}
                fullWidth
              >
                Proceed
              </Button>
            </Grid.Col>
            <Grid.Col span={{ md: 4 }}>
              <Link to="/user">
                <Button variant="default" disabled={isSubmitting} fullWidth>
                  Cancel
                </Button>
              </Link>
            </Grid.Col>
          </Grid>
        </form>
      </Paper>
    </>
  );
};

export default Welcome;
